import { History, Redo, Undo } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useState } from 'react'; // <-- Imported useState and useEffect
import { useIntl } from 'react-intl';

import { Box, ClickAwayListener, Modal } from '@mui/material';
import { EActiveDrawer, EWallMode } from '../../../../@types/Enums';
import useUndoRedo from '../../../../hooks/useUndoRedo';
import { useBuilding } from '../../../../provider/BuildingProvider/BuildingProvider';
import { useHistory } from '../../../../provider/HistoryProvider';
import { useSelection } from '../../../../provider/SelectionProvider';
import { useToolbarContext } from '../../../../provider/ToolbarProvider';
import { secondaryIconActive, secondaryIconHover, secondaryIconInactive } from '../../../../utils/colors';

const menuIconStyle = {
  maxWidth: '1em',
  maxHeight: '1em',
  margin: '.1rem 1rem',
  transition: '.2s ease-in-out',
};

interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  buttonPosition?: DOMRect;
}

const HistoryModal: React.FC<HistoryModalProps> = ({ open, onClose, buttonPosition }) => {
  // Hooks
  const { activeDrawer, setActiveDrawer } = useSelection();
  const { setWallMode } = useToolbarContext();
  const { resetSelectionWithoutHistoryDrawer: resetSelectionWithoutDrawer, resetSelection } = useSelection();
  const { setBuilding } = useBuilding();
  const { undo, redo } = useUndoRedo();
  const { history, currentIndex } = useHistory();
  const intl = useIntl();

  // New state to determine OS
  const [isMac, setIsMac] = useState(false);

  // Determine the OS on component mount
  useEffect(() => {
    // Check if the platform string includes 'Mac'
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') > -1);
  }, []);

  // Hotkey strings based on OS
  const undoHotkey = isMac ? '[Cmd + Z]' : '[Ctrl + Z]';
  const redoHotkey = isMac ? '[Cmd + Shift + Z]' : '[Ctrl + Y]'; 
  
  const undoAvailable = useMemo(() => history.length > 0 && currentIndex && currentIndex > 0, [history.length, currentIndex]); // Enabling undos until the first drawing step (currentIndex = 0)
  const redoAvailable = useMemo(() => (currentIndex ?? 0) < history.length - 1, [history.length, currentIndex]); // Enabling redos until currentIndex is less than the previous performed steps

  const handleToggleHistoryDrawer = useCallback(() => {
    setWallMode(EWallMode.NONE);
    resetSelectionWithoutDrawer();
    setActiveDrawer(activeDrawer === EActiveDrawer.HISTORY ? EActiveDrawer.NONE : EActiveDrawer.HISTORY);
    onClose();
  }, [activeDrawer, setActiveDrawer, setWallMode]);

  const handleUndo = useCallback(() => {
    resetSelection();
    setWallMode(EWallMode.NONE);
    undo(setBuilding);
  }, [resetSelection, setBuilding, setWallMode, undo]);

  const handleRedo = useCallback(() => {
    resetSelection();
    setWallMode(EWallMode.NONE);
    redo(setBuilding);
  }, [redo, resetSelection, setBuilding, setWallMode]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 999,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 48}px` : '50%',
        width: 'fit-content',
        transform: 'none',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), ' + '0px 4px 5px 0px rgba(0,0,0,0.14), ' + '0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Title Section */}
            <Box
              sx={{
                padding: '0.5rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.5rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.history' })}
              </Box>
            </Box>

            {/* options List */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                component="div"
                key={'toolbar-historyModal-undo'}
                onClick={undoAvailable ? handleUndo : undefined}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.2rem 0.5rem',
                  fontSize: '1rem',
                  borderRadius: 0,
                  cursor: !undoAvailable ? 'default' : 'pointer',
                  pointerEvents: !undoAvailable ? 'none' : 'auto',
                  backgroundColor: 'inherit',
                  color: !undoAvailable ? 'rgba(0, 0, 0, 0.1)' : secondaryIconInactive,
                  width: '18rem',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: !undoAvailable ? 'inherit' : secondaryIconHover,
                  },
                  transition: 'background-color 0.2s ease-in-out',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Undo style={menuIconStyle} />
                  <span style={{ marginLeft: '0.5rem' }}>
                    {intl.formatMessage({
                      id: 'Toolbar.undo',
                      defaultMessage: 'Undo',
                    })}
                  </span>
                </Box>
                <Box sx={{ minWidth: '7rem', textAlign: 'right', opacity: 0.5 }}>{undoHotkey}</Box> {/* <-- Used undoHotkey */}
              </Box>

              <Box
                component="div"
                key={'toolbar-historyModal-redo'}
                onClick={redoAvailable ? handleRedo : undefined}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.2rem 0.5rem',
                  fontSize: '1rem',
                  borderRadius: 0,
                  cursor: !redoAvailable ? 'default' : 'pointer',
                  pointerEvents: !redoAvailable ? 'none' : 'auto',
                  backgroundColor: 'inherit',
                  color: !redoAvailable ? 'rgba(0, 0, 0, 0.1)' : secondaryIconInactive,
                  width: '18rem',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: !redoAvailable ? 'inherit' : secondaryIconHover,
                  },
                  transition: 'background-color 0.2s ease-in-out',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Redo style={menuIconStyle} />
                  <span style={{ marginLeft: '0.5rem' }}>
                    {intl.formatMessage({
                      id: 'Toolbar.redo',
                      defaultMessage: 'Redo',
                    })}
                  </span>
                </Box>

                <Box sx={{ minWidth: '7rem', textAlign: 'right', opacity: 0.5 }}>{redoHotkey}</Box> {/* <-- Used redoHotkey */}
              </Box>

              {/* Item 2: History */}
              <Box
                component="div"
                key={'toolbar-historyModal-history'}
                onClick={handleToggleHistoryDrawer}
                sx={{
                  display: 'grid',
                  gridTemplateRows: '2rem',
                  gridTemplateColumns: '28% 65%',
                  alignItems: 'center',
                  padding: '0.2rem 0.5rem',
                  fontSize: '1rem',
                  borderRadius: 0,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  backgroundColor: 'inherit',
                  color: activeDrawer === EActiveDrawer.HISTORY ? secondaryIconActive : secondaryIconInactive,
                  width: '18rem',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: secondaryIconHover,
                  },
                  transition: 'background-color 0.2s ease-in-out',
                }}
              >
                <History style={menuIconStyle} />
                <Box sx={{ marginLeft: '-1rem' }}>
                  {intl.formatMessage({
                    id: 'Toolbar.history',
                    defaultMessage: 'History',
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default HistoryModal;